import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, CheckCircle2, CreditCard, ShieldCheck } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import SEO from "../SEO/SEO";
import { post } from "../../utils/request";
import { getTimezone } from "../../utils/context";
import { notifyCartUpdated } from "./CartContext";
import { displayCheckoutTotal, itemSubtitle } from "./cartDisplay";
import "./cart.scss";

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const script = document.createElement("script");
  script.src = "https://checkout.razorpay.com/v1/checkout.js";
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

function storedSelection() {
  try { return JSON.parse(sessionStorage.getItem("teleport_cart_selection") || "[]"); }
  catch { return []; }
}

export default function CartCheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const cartItemIds = useMemo(() => {
    const value = location.state?.cartItemIds || storedSelection();
    return Array.isArray(value) ? value : [];
  }, [location.state]);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [tellyCredit, setTellyCredit] = useState(0);

  useEffect(() => {
    let active = true;
    if (!cartItemIds.length) {
      setLoading(false);
      return undefined;
    }
    post("/combinedOrder/cartCheckout", { cartItemIds })
      .then((result) => active && setDraft(result))
      .catch((error) => active && toast.error(error?.message || "Unable to prepare this checkout."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [cartItemIds]);

  const updateRewards = async () => {
    const preorderId = draft?.preorderInfo?.preorderId;
    if (!preorderId) return;
    setLoading(true);
    try {
      const result = await post("/combinedOrder/update", {
        preorderId,
        promoCode: promoCode.trim(),
        tellyCredit: Number(tellyCredit || 0),
      });
      setDraft((current) => ({
        ...current,
        ...result,
        items: result?.items || current?.items || [],
      }));
      toast.success("Your checkout total has been updated.");
    } catch (error) {
      toast.error(error?.message || "Unable to apply these rewards.");
    } finally {
      setLoading(false);
    }
  };

  const notifyPayment = async (paymentId) => {
    if (!paymentId) return;
    try { await post(`/payment/${paymentId}/notify`); } catch { /* non-blocking */ }
  };

  const openPayment = async () => {
    const preorderId = draft?.preorderInfo?.preorderId;
    if (!preorderId || paying) return;
    setPaying(true);
    try {
      const order = await post("/combinedOrder/submitOrder", { preorderId, timezone: getTimezone() });
      const orderId = order?.orderId;
      const serviceType = order?.serviceType || "combinedOrder";
      if (!orderId) throw new Error("The order could not be created.");
      const payment = await post("/payment/pay", { orderId, serviceType });
      if (payment?.isHit) {
        throw new Error(payment?.riskResult?.message || "Additional account verification is required before payment.");
      }
      const payResult = payment?.payResult;
      if (!payResult) throw new Error("The payment provider did not return a checkout session.");
      const complete = async () => {
        sessionStorage.removeItem("teleport_cart_selection");
        notifyCartUpdated();
        navigate("/my-bookings", { replace: true });
      };
      if (payResult.skipPayment === true) return complete();
      if (!window.Razorpay && !(await loadRazorpayScript())) throw new Error("Payment system failed to load.");
      const razorpay = new window.Razorpay({
        key: payResult.appkey,
        order_id: payResult.providerOrderId,
        amount: payResult.amount,
        currency: payResult.currency,
        name: payResult.name,
        description: payResult.description,
        timeout: payResult.timeout,
        handler: async () => { await notifyPayment(payResult.paymentId); await complete(); },
        prefill: { name: payResult.fullName, email: payResult.email, contact: payResult.contact },
        theme: { color: "#d4af37" },
        modal: { ondismiss: () => setPaying(false) },
      });
      razorpay.on("payment.failed", () => { toast.error("Payment failed. You can retry from My Bookings."); navigate("/my-bookings", { replace: true }); });
      razorpay.open();
    } catch (error) {
      toast.error(error?.message || "Unable to start payment.");
      setPaying(false);
    }
  };

  const cancelDraft = async () => {
    const preorderId = draft?.preorderInfo?.preorderId;
    if (preorderId) {
      try { await post("/combinedOrder/cancelCheckout", { preorderId }); } catch { /* expiry is the fallback */ }
    }
    navigate("/cart");
  };

  return (
    <><SEO title="Secure Checkout | Teleport Now" canonicalPath="/cart/checkout" noindex /><Navbar />
      <main className="cart-checkout-page">
        <button type="button" className="cart-back" onClick={cancelDraft}><ArrowLeft /> Back to cart</button>
        <header><p className="teleport-eyebrow">Secure combined checkout</p><h1>Review and pay</h1><p>One payment for the travel essentials selected in your cart.</p></header>
        {loading && !draft ? <div className="cart-loading">Confirming live prices and availability…</div> : !draft ? (
          <section className="cart-empty"><h2>This checkout is no longer available.</h2><p>Return to your cart to refresh the selected items.</p><Link to="/cart">Return to cart</Link></section>
        ) : (
          <div className="cart-checkout-layout">
            <section className="cart-checkout-items">
              <div className="checkout-trust"><ShieldCheck /><div><strong>Availability confirmed</strong><span>Prices are reserved until the displayed checkout expiry.</span></div></div>
              {(draft.items || []).map((item) => (
                <article key={item.cartItemId}><div><span>{item.serviceType === "thingsToDo" ? "Things To Do" : item.serviceType}</span><h2>{item.itemName}</h2><p>{itemSubtitle(item)}</p></div><strong>{item.priceInfo?.totalPrice?.content || item.priceInfo?.displayFinalAmount || "Included"}</strong></article>
              ))}
              <section className="cart-rewards"><div><p className="teleport-eyebrow">Promo & Telly Credits</p><h2>Use available rewards</h2></div><label>Promo code<input value={promoCode} onChange={(event) => setPromoCode(event.target.value)} placeholder="Enter promo code" /></label><label>Telly Credits<input type="number" min="0" step="0.01" value={tellyCredit} onChange={(event) => setTellyCredit(event.target.value)} /></label><button type="button" onClick={updateRewards} disabled={loading}>Apply</button></section>
            </section>
            <aside className="cart-summary cart-summary--checkout"><p className="teleport-eyebrow">Order summary</p>{(draft.priceInfo?.priceDetails || []).map((detail, index) => <div key={`${detail.title}-${index}`}><span>{detail.title}</span><strong>{detail.content}</strong></div>)}<div className="cart-summary__total"><span>{draft.priceInfo?.totalPrice?.title || "Total"}</span><strong>{displayCheckoutTotal(draft.priceInfo)}</strong></div><button type="button" onClick={openPayment} disabled={paying || loading}><CreditCard /> {paying ? "Opening secure payment…" : "Pay securely"}</button><p className="cart-summary__assurance"><CheckCircle2 /> Encrypted Razorpay payment</p></aside>
          </div>
        )}
      </main><Footer /></>
  );
}
